export class SubscribableCache {
    /**
     * Reads an entry from the cache.
     * If the entry does not exist, returns null.
     *
     * @param key The key of the entry to read.
     * @returns The entry, or null if it does not exist.
     */
    async read(key) {
        return this.readEntry(key);
    }
    /**
     * Writes an entry to the cache.
     * If the entry already exists, it will be overwritten.
     *
     * @param key The key of the entry to write.
     * @param entry The entry to write.
     * @returns A promise that resolves when the entry has been written.
     * @throws If the entry could not be written.
     */
    async write(key, entry) {
        return this.writeEntry(key, entry);
    }
    /**
     * Deletes an entry from the cache.
     * If the entry does not exist, this method does nothing.
     *
     * @param key The key of the entry to delete.
     * @returns A promise that resolves when the entry has been deleted.
     */
    async delete(key) {
        return this.deleteEntry(key);
    }
    /**
     * Reads an entry from the cache. If it does not exist, calls the onMiss
     * function to generate the entry, writes it to the cache, and returns it.
     *
     * @param key The key of the entry to read.
     * @param onMiss A function that generates the entry if it does not exist.
     */
    async fetch(key, onMiss) {
        let entry = await this.read(key);
        if (entry === null) {
            entry = await onMiss(key);
            if (entry !== null) {
                await this.write(key, entry);
            }
        }
        return entry;
    }
    /**
     * Reads multiple entries from the cache. For any entries that do not exist,
     * calls the onMiss function to generate them, then writes them to the cache.
     * Returns all requested entries in the same order as the given keys.
     *
     * @param keys The keys of the entries to read.
     * @param onMiss A function that generates the missing entries and returns a Map of keys to entries.
     * @returns An array of entries in the same order as the given keys.
     */
    async fetchMultiple(keys, onMiss) {
        const entries = new Map();
        const missingKeys = [];
        for (const key of keys) {
            // QUESTION: why do this in serial instead of parallel with Promise.all?
            const entry = await this.read(key);
            if (entry === null) {
                missingKeys.push(key);
            }
            else {
                entries.set(key, entry);
            }
        }
        if (missingKeys.length > 0) {
            const missingEntries = await onMiss(missingKeys);
            for (const [key, entry] of missingEntries) {
                entries.set(key, entry);
                await this.write(key, entry);
            }
        }
        return keys.map((key) => entries.get(key));
    }
    /**
     * Subscribes to changes to an entry in the cache.
     * The onChange function will be called whenever the entry changes.
     *
     * A deleted or nonexistent entry will be represented as null.
     *
     * @param key The key of the entry to watch.
     * @param onChange The function to call when the entry changes.
     * @returns A function that unsubscribes the listener.
     */
    subscribe(key, onChange) {
        return this.watchEntry(key, onChange);
    }
}
//# sourceMappingURL=index.js.map